import React, { useEffect, useState } from 'react';
import { Alert } from '@mui/material';

/**
 * Browsers refuse to play audio on a freshly loaded page until the user has clicked
 * or typed on it once, so a new-order alert that arrives after a reload is silent.
 * This banner says so, and disappears on the first click/keypress (which is also what
 * unlocks the audio). `navigator.userActivation` is sticky for the page's lifetime, so
 * moving between dash pages after that first click does not bring it back.
 */
function hasBeenActivated(): boolean {
  const activation = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } })
    .userActivation;
  return activation?.hasBeenActive ?? false;
}

export default function SoundUnlockBanner({ muted }: { muted: boolean }) {
  const [needsClick, setNeedsClick] = useState(() => !hasBeenActivated());

  useEffect(() => {
    if (!needsClick) return;
    const unlock = () => setNeedsClick(false);
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [needsClick]);

  if (!needsClick || muted) return null;

  return (
    <Alert severity="warning" sx={{ mb: 2, cursor: 'pointer' }}>
      Click anywhere on this page to turn on the new-order sound. Browsers keep a page silent
      until it has been clicked once after loading.
    </Alert>
  );
}
